import React from "react";
import { Alert, AlertTitle, Button } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import type { AlertColor } from "@mui/material/Alert";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * The four mastery signal levels that can surface on a goal/target card or detail view.
 *
 * - `allTargetsMet`       — every target under the goal has met mastery criteria (green)
 * - `approachingMastery`  — the last active target is nearing the mastery threshold (blue/info)
 * - `approachingRegression` — recent sessions are nearing the regression threshold (amber)
 * - `progressStalled`     — no measurable progress for 2+ weeks (amber)
 * - `regression`          — consecutive sessions below the regression threshold (red)
 */
export type MasteryBannerLevel =
  | "allTargetsMet"
  | "approachingMastery"
  | "approachingRegression"
  | "progressStalled"
  | "regression";

export interface MasteryBannerProps {
  /** The mastery signal type — determines severity color */
  level: MasteryBannerLevel;
  /**
   * Optional title text shown in the banner header.
   * When omitted, only `detail` is rendered as a single-line message.
   */
  title?: string;
  /**
   * Supporting context shown below the title.
   * E.g. "2 of 3 targets met, 1 approaching"
   */
  detail?: string;
  /** Called when the user clicks the Dismiss button */
  onDismiss?: () => void;
  /** Additional MUI sx overrides for the Alert root */
  sx?: SxProps<Theme>;
}

// ---------------------------------------------------------------------------
// Configuration — maps level to alert severity only
// ---------------------------------------------------------------------------

const BANNER_CONFIG: Record<MasteryBannerLevel, AlertColor> = {
  allTargetsMet: "success",
  approachingMastery: "info",
  approachingRegression: "warning",
  progressStalled: "warning",
  regression: "error",
};

// ---------------------------------------------------------------------------
// MasteryBanner
// ---------------------------------------------------------------------------

/**
 * Dismissible banner that surfaces mastery readiness or attention signals for
 * a goal or target. Informational only — does not trigger any status changes.
 *
 * Shared between Goal detail and Target detail views.
 *
 * Renders in five severity styles:
 * - **success** (green): `allTargetsMet`
 * - **info** (blue):     `approachingMastery`
 * - **warning** (amber): `approachingRegression`
 * - **warning** (amber): `progressStalled`
 * - **error** (red):     `regression`
 *
 * When `title` is omitted, the banner collapses to a single-line message
 * showing only `detail`.
 */
const MasteryBanner: React.FC<MasteryBannerProps> = ({ level, title, detail, onDismiss, sx }) => {
  const severity = BANNER_CONFIG[level];
  const hasTitle = Boolean(title);

  return (
    <Alert
      severity={severity}
      variant="standard"
      action={
        onDismiss && (
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={onDismiss}
            aria-label="Dismiss mastery banner"
            sx={{ whiteSpace: "nowrap", alignSelf: "flex-start", mt: "2px" }}
          >
            Dismiss
          </Button>
        )
      }
      sx={sx}
    >
      {hasTitle && <AlertTitle sx={{ mb: detail ? 1 : 0 }}>{title}</AlertTitle>}
      {detail}
    </Alert>
  );
};

export default MasteryBanner;
